import React, { useMemo, useState } from 'react';
import { ChartLine } from 'lucide-react';
import { Panel, Pill, money, num } from './pipelineParts';
import { CHART_PALETTE } from './trackerKit';
import { TrailingChip } from './PeriodBand';
import MovementTrendChart, { type MovementTrendPoint } from './MovementTrendChart';
import { useMovementTrendReport } from '../../../hooks/pipelineTracker/useMovementTrendReport';
import {
  TARGET_STAGES,
  attainment,
  monthLabel,
  windowCountTarget,
  windowValueTargetCents,
  type MovementTrendMonth,
  type MovementTrendResponse,
  type TargetScope,
  type TargetStageKey,
} from '../../../hooks/pipelineTracker/targetsContract';

/**
 * Deal movements: 12-month trend vs target.
 *
 * The Deal movements row answers "how is this window going"; this answers "is that the usual
 * shape", one point per calendar month for the 12 months ending at the snapshot's month. Same four
 * dated events as the row (Leads, Submissions, Approvals, Settlements), same $ / # toggle, dollars
 * first (Evoke, 25 Sep 2026: the stage picker lists the same stages as the row).
 *
 * WHY THE LAST MONTH IS SHORT. The export stops on its as-at date, so the newest month runs to that
 * day and no further. Its target is pro-rated to the same days by the rule the band states, so a
 * half-month is never graded against a whole month's target.
 *
 * NO FIGURE WITHOUT A SOURCE. Every point is a count of dated events in the committed snapshot;
 * the target line is the tenant's own monthly target and is drawn only where one exists. A scope
 * with no target (a broker without an override, a book, a filtered slice) draws the actual line
 * alone and says why.
 */

type Mode = '$' | '#';

/** The Deal movements tiles' colours (PeriodBand MOVEMENTS), so a stage is one colour on the tab. */
const STAGE_COLOUR: Record<TargetStageKey, string> = {
  leads: CHART_PALETTE[0],
  submissions: CHART_PALETTE[1],
  approvals: CHART_PALETTE[3],
  settlements: CHART_PALETTE[2],
};

const SEGMENT = (on: boolean): string =>
  `rounded-md px-2.5 py-1 text-[0.6875rem] font-bold transition-colors ${
    on ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
  }`;

const shortMonth = (key: string): string => {
  const label = monthLabel(key); // 'Sep 2026'
  return label.length === 8 ? `${label.slice(0, 3)} ${label.slice(6)}` : label;
};

/** The chart's rows for one stage and unit. Exported for the tests. */
export function trendPoints(
  months: readonly MovementTrendMonth[],
  stage: TargetStageKey,
  mode: Mode,
  targets: TargetScope | undefined,
): MovementTrendPoint[] {
  const meta = TARGET_STAGES.find((s) => s.key === stage)!;
  const stageTarget = targets && targets.kind !== 'none' ? targets.stages[stage] : undefined;
  return months.map((m) => ({
    label: shortMonth(m.month),
    actual: mode === '$' ? m[meta.flow].valueCents : m[meta.flow].count,
    target:
      mode === '$'
        ? windowValueTargetCents(stageTarget?.monthlyValueCents, m.from, m.to)
        : windowCountTarget(stageTarget?.monthlyCount, m.from, m.to),
    partial: m.partial,
  }));
}

export interface MovementTrendViewProps {
  response: MovementTrendResponse | null;
  loading: boolean;
  error: string | null;
  targets?: TargetScope;
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
}

export const MovementTrendView: React.FC<MovementTrendViewProps> = ({ response, loading, error, targets, accent }) => {
  const [stage, setStage] = useState<TargetStageKey>('settlements');
  const [mode, setMode] = useState<Mode>('$');

  const months = useMemo(() => (response?.status === 'ok' ? response.months : []), [response]);
  const points = useMemo(() => trendPoints(months, stage, mode, targets), [months, stage, mode, targets]);
  const meta = TARGET_STAGES.find((s) => s.key === stage)!;
  const format = (v: number): string => (mode === '$' ? money(v) : num(Math.round(v * 10) / 10));
  const showTarget = points.some((p) => p.target !== null);
  // The stage carries a target in the OTHER unit only (Leads is usually a count target). Said, with
  // the switch offered, rather than "no target" when one exists a click away.
  const otherMode: Mode = mode === '$' ? '#' : '$';
  const otherUnitHasTarget =
    !showTarget && trendPoints(months, stage, otherMode, targets).some((p) => p.target !== null);
  const latest = points[points.length - 1];
  const latestMonth = months[months.length - 1];
  const grade =
    latest && targets && targets.kind !== 'none'
      ? attainment(latest.actual, latest.target, targets.rag)
      : null;

  const controls = (
    <div className="ml-auto flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5" role="group" aria-label="Trend stage">
        {TARGET_STAGES.map((s) => (
          <button key={s.key} type="button" aria-pressed={s.key === stage} onClick={() => setStage(s.key)} className={SEGMENT(s.key === stage)}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5" role="group" aria-label="Show trend as">
        {(['$', '#'] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={m === mode}
            aria-label={m === '$' ? 'Trend in dollars' : 'Trend in deal counts'}
            onClick={() => setMode(m)}
            className={SEGMENT(m === mode)}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (error) {
    body = <p className="text-sm text-amber-300">{error}</p>;
  } else if (loading && response === null) {
    body = <p className="text-sm text-gray-400">Loading the last 12 months…</p>;
  } else if (response === null || response.status !== 'ok') {
    body = (
      <p className="text-sm text-gray-400">
        {response?.status === 'invalid_request'
          ? `The trend could not be built: ${response.reason}`
          : 'No committed snapshot yet, so there are no months to draw.'}
      </p>
    );
  } else {
    body = (
      <>
        <MovementTrendChart
          data={points}
          colour={STAGE_COLOUR[stage]}
          stageLabel={meta.label}
          format={format}
          showTarget={showTarget}
        />
        <p className="mt-3 text-xs leading-relaxed text-gray-500">
          Events dated in each calendar month, both ends included, counted from the same export as the
          Deal movements row, so the months from July add up to its FYTD figures.
          {latestMonth?.partial ? ` ${monthLabel(latestMonth.month)} runs to the snapshot date, not the month end.` : ''}{' '}
          {showTarget
            ? 'The dashed line is the monthly target, pro rata by days for a part month.'
            : targets && targets.kind === 'none'
              ? `${targets.message}, so only the actual line is drawn.`
              : otherUnitHasTarget
                ? (
                  <>
                    {meta.label} has a {otherMode === '#' ? 'count' : 'dollar'} target, not a{' '}
                    {mode === '$' ? 'dollar' : 'count'} one.{' '}
                    <button
                      type="button"
                      onClick={() => setMode(otherMode)}
                      className="font-semibold text-blue-300 hover:text-blue-200"
                      data-testid="trend-switch-unit"
                    >
                      Show {otherMode === '#' ? 'deal counts' : 'dollars'} against it
                    </button>
                  </>
                )
                : `No ${mode === '$' ? 'dollar' : 'count'} target is set for ${meta.label.toLowerCase()}, so only the actual line is drawn.`}
        </p>
      </>
    );
  }

  const blurb =
    latest && latestMonth && response?.status === 'ok'
      ? `${meta.label}, last 12 months. ${monthLabel(latestMonth.month)}${latestMonth.partial ? ' so far' : ''}: ${format(latest.actual)}${
          latest.target !== null ? ` against a target of ${format(latest.target)}` : ''
        }.`
      : `${meta.label}, last 12 months.`;

  return (
    <Panel
      title="Deal movements: 12-month trend vs target"
      icon={ChartLine}
      accent={accent}
      badge={
        <>
          <TrailingChip>12 months to {latestMonth ? monthLabel(latestMonth.month) : 'the snapshot'}</TrailingChip>
          {grade && (
            <Pill tone={grade.tone === 'green' ? 'good' : grade.tone === 'amber' ? 'warn' : 'bad'}>
              Latest month {grade.pct}% of target
            </Pill>
          )}
          {controls}
        </>
      }
      blurb={blurb}
    >
      {body}
    </Panel>
  );
};

export interface MovementTrendSectionProps {
  companyId: string;
  asAtDate?: string;
  adviserId?: string;
  groupKey?: string;
  workflowName?: string;
  dealLabel?: string;
  targets?: TargetScope;
  accent?: string;
}

/** The section as the tab mounts it: the same scope as the report above it. */
const MovementTrendSection: React.FC<MovementTrendSectionProps> = ({ targets, accent, ...scope }) => {
  const { response, loading, error } = useMovementTrendReport(scope);
  return <MovementTrendView response={response} loading={loading} error={error} targets={targets} accent={accent} />;
};

export default MovementTrendSection;
